import { useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FileText, Film, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/hooks/useToast';
import { uploadCatalogImage } from '@/api/installation.api';
import {
  listarProyectos,
  crearProyecto,
  editarProyecto,
  borrarProyecto,
  subirBrochure,
  subirVideo,
  type Proyecto,
  type DatosDeProyecto,
} from '@/api/proyectos.api';
import { aDatos, aFormulario, enTramo, FORMULARIO_VACIO, MAX_VIDEOS, TRAMOS_DE_PRECIO, urgencia, venceEnTexto, VIGENCIAS, type Formulario } from '@/lib/proyectos';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { conCuentas, opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';

const VALIDOS = { precio: TRAMOS_DE_PRECIO.map((t) => t.valor), vigencia: VIGENCIAS.map((v) => v.valor) };

/**
 * PROYECTOS DE RICHARD (tanda 3, Ola D; P10).
 *
 * Sus fichas con hasta cuándo vale cada precio. El agente nombra solo los vigentes,
 * y nunca dice el precio: aquí el precio es para Richard. «Vence en N días» le
 * recuerda actualizarlo antes de que el agente deje de nombrarlo.
 */

const COLOR_DE_URGENCIA = {
  vencido: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  pronto: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  bien: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
} as const;

const ENTRADA = 'min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900';

/** Una ficha. Sin estado: se prueba con renderToStaticMarkup. */
export function ProyectoTarjeta({
  p,
  onEditar,
  onBorrar,
  onBrochure,
}: {
  p: Proyecto;
  onEditar?: () => void;
  onBorrar?: () => void;
  onBrochure?: (archivo: File) => void;
}) {
  const detalle = [
    `${p.zona} · desde US$ ${p.precioDesdeUsd.toLocaleString('en-US')}`,
    p.inicialMinimaPct != null ? `inicial ${p.inicialMinimaPct} %` : null,
    p.entrega ? `entrega ${p.entrega}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-slate-900 dark:text-slate-50">{p.nombre}</p>
          <Badge className={COLOR_DE_URGENCIA[urgencia(p.venceEnDias)]}>{venceEnTexto(p.venceEnDias)}</Badge>
          {p.noRecomendar && <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">No recomendar</Badge>}
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">{detalle}</p>
        {p.amenidades.length > 0 && <p className="text-xs text-slate-500">{p.amenidades.join(' · ')}</p>}
        {p.fotos.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {p.fotos.map((f) => (
              <img key={f.key} src={f.url} alt="" className="h-16 w-24 shrink-0 rounded object-cover" />
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500">
          {p.vigente && !p.noRecomendar ? 'El agente lo puede nombrar: solo el nombre, nunca el precio.' : 'El agente no lo nombra.'}
        </p>
        {(p.videos.length > 0 || p.enlace) && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
            {p.videos.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Film className="h-4 w-4" /> {p.videos.length === 1 ? '1 video' : `${p.videos.length} videos`}
              </span>
            )}
            {p.enlace && (
              <a href={p.enlace} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-1 break-all text-brand-700 underline dark:text-brand-300">
                <ExternalLink className="h-4 w-4 shrink-0" /> {p.enlace}
              </a>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          {p.brochureUrl && (
            <a href={p.brochureUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-brand-700 underline dark:text-brand-300">
              <FileText className="h-4 w-4" /> Brochure
            </a>
          )}
          {onBrochure && (
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 text-sm text-slate-600 underline dark:text-slate-300">
              <Upload className="h-4 w-4" /> {p.brochureUrl ? 'Cambiar el brochure' : 'Subir el brochure (PDF)'}
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onBrochure(f);
                }}
              />
            </label>
          )}
          {onEditar && (
            <Button size="sm" variant="outline" onClick={onEditar}>
              <Pencil className="h-4 w-4" /> Editar
            </Button>
          )}
          {onBorrar && (
            <Button size="sm" variant="outline" onClick={onBorrar}>
              <Trash2 className="h-4 w-4" /> Borrar
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

type CampoDeTexto = 'nombre' | 'zona' | 'precioDesdeUsd' | 'inicialMinimaPct' | 'entrega' | 'amenidades' | 'vigenteHasta' | 'enlace';

/** El formulario de un proyecto. Exportado para probar su vista con renderToStaticMarkup. */
export function FormularioDeProyecto({
  inicial,
  zonas,
  guardando,
  onGuardar,
  onCancelar,
}: {
  inicial: Formulario;
  zonas: string[];
  guardando: boolean;
  onGuardar: (d: DatosDeProyecto) => void;
  onCancelar: () => void;
}) {
  const toast = useToast();
  const [f, setF] = useState<Formulario>(inicial);
  const poner = (k: CampoDeTexto) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const subirFoto = async (archivo: File) => {
    try {
      const r = await uploadCatalogImage(archivo);
      setF((x) => ({ ...x, fotos: [...x.fotos, r.key] }));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const [subiendoVideo, setSubiendoVideo] = useState(false);
  const agregarVideo = async (archivo: File) => {
    setSubiendoVideo(true);
    try {
      const r = await subirVideo(archivo);
      setF((x) => ({ ...x, videos: [...x.videos, r.key] }));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSubiendoVideo(false);
    }
  };
  const guardar = () => {
    const r = aDatos(f);
    if ('error' in r) toast.error(r.error);
    else onGuardar(r.datos);
  };
  return (
    <Card>
      <CardBody className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Nombre</span>
          <input value={f.nombre} onChange={poner('nombre')} className={ENTRADA} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Zona</span>
          <input list="zonas-de-proyecto" value={f.zona} onChange={poner('zona')} className={ENTRADA} />
          <datalist id="zonas-de-proyecto">
            {zonas.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Precio desde (US$)</span>
            <input inputMode="numeric" value={f.precioDesdeUsd} onChange={poner('precioDesdeUsd')} className={ENTRADA} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Inicial mínima (%)</span>
            <input inputMode="numeric" value={f.inicialMinimaPct} onChange={poner('inicialMinimaPct')} className={ENTRADA} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Entrega</span>
            <input placeholder="2027, lista…" value={f.entrega} onChange={poner('entrega')} className={ENTRADA} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">El precio vale hasta</span>
            <input type="date" value={f.vigenteHasta} onChange={poner('vigenteHasta')} className={ENTRADA} />
          </label>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Amenidades (una por línea)</span>
          <textarea rows={3} value={f.amenidades} onChange={poner('amenidades')} className={`${ENTRADA} py-2`} />
        </label>
        <label className="flex min-h-[44px] items-center gap-2 text-sm">
          <input type="checkbox" checked={f.noRecomendar} onChange={(e) => setF((x) => ({ ...x, noRecomendar: e.target.checked }))} />
          No recomendar: el agente no lo nombra
        </label>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
          <span>{f.fotos.length === 1 ? '1 foto' : `${f.fotos.length} fotos`}</span>
          <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 underline">
            <Upload className="h-4 w-4" /> Agregar una foto
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const a = e.target.files?.[0];
                if (a) void subirFoto(a);
              }}
            />
          </label>
          {f.fotos.length > 0 && (
            <button type="button" className="min-h-[44px] underline" onClick={() => setF((x) => ({ ...x, fotos: [] }))}>
              Quitar las fotos
            </button>
          )}
        </div>
        <div className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
          <p className="font-medium text-slate-900 dark:text-slate-100">Videos (MP4 o MOV, hasta 25 MB cada uno)</p>
          {f.videos.length === 0 && <p className="text-xs text-slate-500">Sin videos.</p>}
          {f.videos.map((k, i) => (
            <div key={k} className="flex flex-wrap items-center gap-3">
              <a href={`/media/${k}`} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1 underline">
                <Film className="h-4 w-4" /> Video {i + 1}
              </a>
              <button type="button" className="min-h-[44px] underline" onClick={() => setF((x) => ({ ...x, videos: x.videos.filter((v) => v !== k) }))}>
                Quitar
              </button>
            </div>
          ))}
          {f.videos.length < MAX_VIDEOS && (
            <label className={`inline-flex min-h-[44px] items-center gap-1 underline ${subiendoVideo ? 'opacity-60' : 'cursor-pointer'}`}>
              <Upload className="h-4 w-4" /> {subiendoVideo ? 'Subiendo el video…' : 'Agregar un video'}
              <input
                type="file"
                accept="video/mp4,video/quicktime"
                className="hidden"
                disabled={subiendoVideo}
                onChange={(e) => {
                  const a = e.target.files?.[0];
                  e.target.value = '';
                  if (a) void agregarVideo(a);
                }}
              />
            </label>
          )}
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Enlace del proyecto (su página en tu web)</span>
          <input type="url" inputMode="url" placeholder="https://…" value={f.enlace} onChange={poner('enlace')} className={ENTRADA} />
        </label>
        <div className="flex gap-2">
          <Button disabled={guardando || subiendoVideo} onClick={guardar}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function Proyectos() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['proyectos'], queryFn: listarProyectos });
  const [editando, setEditando] = useState<Proyecto | 'nuevo' | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['proyectos'] });
  const fallo = (e: Error) => toast.error(e.message);

  const guardar = useMutation({
    mutationFn: (d: DatosDeProyecto) => (editando && editando !== 'nuevo' ? editarProyecto(editando.id, d) : crearProyecto(d)),
    onSuccess: () => {
      setEditando(null);
      refrescar();
      toast.success('Proyecto guardado');
    },
    onError: fallo,
  });
  const borrar = useMutation({ mutationFn: borrarProyecto, onSuccess: refrescar, onError: fallo });
  const brochure = useMutation({
    mutationFn: (v: { id: string; archivo: File }) => subirBrochure(v.id, v.archivo),
    onSuccess: refrescar,
    onError: fallo,
  });

  const todos = data?.proyectos ?? [];
  // SUPERFILTRO (Josué, 14-oct): zona, precio y vigencia, en la URL.
  const { filtros, ponerVarios } = useFiltrosEnUrl({ buscar: '', zona: '', precio: '', vigencia: '' }, VALIDOS);
  const facetas: Faceta[] = [
    { clave: 'zona', titulo: 'Zona', todas: 'Todas', opciones: opcionesDe(todos, (p) => p.zona) },
    { clave: 'precio', titulo: 'Precio desde', opciones: conCuentas(TRAMOS_DE_PRECIO.map(({ valor, etiqueta }) => ({ valor, etiqueta })), todos, (p, v) => enTramo(p.precioDesdeUsd, v)) },
    { clave: 'vigencia', titulo: 'Vigencia', todas: 'Todas', opciones: conCuentas(VIGENCIAS.map(({ valor, etiqueta }) => ({ valor, etiqueta })), todos, (p, v) => urgencia(p.venceEnDias) === v) },
  ];
  const q = filtros.buscar.trim().toLowerCase();
  const proyectos = todos.filter(
    (p) =>
      (!q || `${p.nombre} ${p.zona} ${p.amenidades.join(' ')}`.toLowerCase().includes(q)) &&
      (!filtros.zona || p.zona === filtros.zona) &&
      (!filtros.precio || enTramo(p.precioDesdeUsd, filtros.precio)) &&
      (!filtros.vigencia || urgencia(p.venceEnDias) === filtros.vigencia),
  );
  return (
    <div className="space-y-3">
      <PageHeader
        title="Proyectos"
        description="Tus proyectos y hasta cuándo vale cada precio. El agente nombra solo los vigentes, y nunca dice el precio."
        action={
          editando ? undefined : (
            <Button onClick={() => setEditando('nuevo')}>
              <Plus className="h-4 w-4" /> Nuevo proyecto
            </Button>
          )
        }
      />
      {editando && (
        <FormularioDeProyecto
          key={editando === 'nuevo' ? 'nuevo' : editando.id}
          inicial={editando === 'nuevo' ? FORMULARIO_VACIO : aFormulario(editando)}
          zonas={data?.zonas ?? []}
          guardando={guardar.isPending}
          onGuardar={(d) => guardar.mutate(d)}
          onCancelar={() => setEditando(null)}
        />
      )}
      {isLoading && <p className="text-sm text-slate-500">Cargando…</p>}
      {!isLoading && todos.length > 0 && (
        <Superfiltro
          buscar={filtros.buscar}
          onBuscar={(v) => ponerVarios({ buscar: v })}
          placeholder="Buscar por nombre, zona o amenidad…"
          facetas={facetas}
          valores={filtros}
          onCambiar={ponerVarios}
          onLimpiar={() => ponerVarios(sinFiltros(facetas))}
          resultados={textoDeResultados(proyectos.length, todos.length, ['proyecto', 'proyectos'])}
        />
      )}
      {!isLoading && todos.length > 0 && proyectos.length === 0 && (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Ningún proyecto coincide con la búsqueda o los filtros.
        </p>
      )}
      {!isLoading && todos.length === 0 && (
        <p className="text-sm text-slate-500">Todavía no hay proyectos. Agrega el primero con su precio desde y hasta cuándo vale.</p>
      )}
      {proyectos.map((p) => (
        <ProyectoTarjeta
          key={p.id}
          p={p}
          onEditar={() => setEditando(p)}
          onBorrar={() => {
            if (window.confirm(`¿Borrar «${p.nombre}»?`)) borrar.mutate(p.id);
          }}
          onBrochure={(archivo) => brochure.mutate({ id: p.id, archivo })}
        />
      ))}
    </div>
  );
}
